import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';

/**
 * A functional component that renders a form with form validation and submission handling.
 * @param {Object} props - The props object containing the onSubmit and init functions.
 * @param {Function} props.onSubmit - The function to be called when the form is submitted.
 * @param {Function} props.init - The function to be called to initialize the form.
 * @returns The rendered form component.
 */
function HookForm({ onSubmit = () => {}, init, defaultForm = {}, ...props }) {

    /**
     * Initializes a form using the useForm hook from the react-hook-form library.
     * @param {Object} props - The props object containing the default form values.
     * @param {Object} props.defaultForm - The default values for the form.
     * @returns An object containing the form methods and state.
     */
    const method = useForm({
        mode: 'onChange',
        defaultValues: defaultForm,
        criteriaMode: 'all'
    });

    /**
     * Runs the init function with the given method when the component mounts or when the init or method
     * dependencies change.
     * @param {Function} init - The initialization function to run.
     * @param {string} method - The method to pass to the initialization function.
     * @returns None
     */
    useEffect(() => {
        init && init(method);
    }, [init, method]);

    return (
        <form onSubmit={method.handleSubmit(onSubmit)}>
            {props.children(method)}
        </form>
    );
}

export default HookForm;
